import { useState } from 'react';
import { supabase } from '@/lib/supabase';

export default function InquirySection() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');
    const form = e.currentTarget;
    const data = new FormData(form);

    const contact_name = data.get('contact_name')?.toString() || '';
    const phone = data.get('phone')?.toString() || '';
    const email = data.get('email')?.toString() || '';
    const company = data.get('company')?.toString() || '';
    const interest_category = data.get('interest_category')?.toString() || '';
    const message = data.get('message')?.toString() || '';

    try {
      const { error } = await supabase.from('inquiries').insert({
        contact_name,
        phone,
        email,
        company: company || null,
        interest_category: interest_category || null,
        message,
        status: 'pending',
      });
      if (error) throw error;
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  const inputStyle = {
    backgroundColor: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.12)',
    color: '#fff',
    borderRadius: '8px',
    padding: '10px 12px',
    fontSize: '14px',
    width: '100%',
    outline: 'none',
  };

  return (
    <section
      id="inquiry"
      className="py-16 md:py-24 px-4 md:px-8 lg:px-12"
      style={{ backgroundColor: '#162233', scrollMarginTop: 'calc(108px + var(--banner-height, 0px))' }}
    >
      <div className="max-w-screen-xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left Info */}
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3" style={{ color: '#D4A853' }}>Product Inquiry / 商品询价</p>
            <h2 className="text-3xl md:text-4xl font-black leading-tight mb-2 text-white">
              商品询价 &amp;<br />贸易咨询
            </h2>
            <p className="text-lg font-medium mb-3" style={{ color: 'rgba(255,255,255,0.4)' }}>상품 문의 &amp; 거래 상담</p>
            <p className="text-sm leading-relaxed mb-8" style={{ color: 'rgba(255,255,255,0.6)' }}>
              如有感兴趣的商品或贸易条件，请随时联系我们。<br />
              工作人员将在1~2个工作日内与您联系。<br />
              <span style={{ color: 'rgba(255,255,255,0.35)' }}>관심 상품이나 거래 조건에 대해 문의해 주세요.</span><br />
              <span style={{ color: 'rgba(255,255,255,0.35)' }}>담당자가 영업일 기준 1~2일 이내 연락드립니다.</span>
            </p>

            <div className="space-y-4 mb-8">
              {[
                { icon: 'ri-phone-line', titleZh: '电话咨询', titleKo: '전화 문의', line1: '02-300-1234', line2Zh: '工作日 09:00–18:00', line2Ko: '평일 09:00–18:00' },
                { icon: 'ri-mail-line', titleZh: '邮件咨询', titleKo: '이메일 문의', line1: 'contact@koreab2b.com', line2Zh: '24小时均可受理', line2Ko: '24시간 접수 가능' },
                { icon: 'ri-kakao-talk-fill', titleZh: 'KakaoTalk咨询', titleKo: '카카오톡 상담', line1: '채널명: 코리아B2B', line2Zh: '工作日 09:00–18:00', line2Ko: '평일 09:00–18:00' },
              ].map(item => (
                <div key={item.titleZh} className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl shrink-0 mt-0.5"
                    style={{ background: 'rgba(212,168,83,0.15)', border: '1px solid rgba(212,168,83,0.25)' }}>
                    <i className={`${item.icon} text-sm`} style={{ color: '#D4A853' }}></i>
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-sm text-white leading-tight">
                      {item.titleZh} <span className="font-normal text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>/ {item.titleKo}</span>
                    </p>
                    <p className="text-xs mt-0.5 break-all" style={{ color: 'rgba(255,255,255,0.6)' }}>{item.line1}</p>
                    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                      {item.line2Zh} <span style={{ color: 'rgba(255,255,255,0.25)' }}>/ {item.line2Ko}</span>
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-xl p-4 sm:p-5" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <p className="text-xs font-bold uppercase tracking-wider mb-3 text-white">交易说明 / 거래 안내</p>
              <ul className="space-y-2.5">
                {[
                  { zh: '最低订购量：1箱以上', ko: '최소 주문 수량: 1박스 이상' },
                  { zh: '付款：开票后转账', ko: '결제: 세금계산서 발행 후 계좌이체' },
                  { zh: '配送：全国次日达', ko: '배송: 전국 익일 배송 (도서·산간 제외)' },
                  { zh: '大量订购可协商单价', ko: '대량 주문 시 단가 협의 가능' },
                ].map(item => (
                  <li key={item.zh} className="flex items-start gap-2">
                    <div className="w-3.5 h-3.5 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-check-line text-xs font-bold" style={{ color: '#D4A853' }}></i>
                    </div>
                    <div>
                      <p className="text-xs leading-snug" style={{ color: 'rgba(255,255,255,0.65)' }}>{item.zh}</p>
                      <p className="text-[10px] leading-snug" style={{ color: 'rgba(255,255,255,0.3)' }}>{item.ko}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right Form */}
          <div className="rounded-2xl p-6 md:p-8" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
            {status === 'success' ? (
              <div className="py-12 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full mx-auto mb-4"
                  style={{ background: 'rgba(212,168,83,0.2)', border: '1px solid rgba(212,168,83,0.4)' }}>
                  <i className="ri-check-line text-3xl" style={{ color: '#D4A853' }}></i>
                </div>
                <h3 className="text-xl font-black mb-2 text-white">询价已提交 / 문의가 접수되었습니다</h3>
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>工作人员将在1~2个工作日内联系您。<br /><span>영업일 기준 1~2일 이내 연락드리겠습니다.</span></p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-6 px-6 py-2.5 text-sm font-bold rounded-xl cursor-pointer whitespace-nowrap transition-opacity hover:opacity-85"
                  style={{ backgroundColor: '#D4A853', color: '#162233' }}
                >
                  重新询价 / 새 문의 작성
                </button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                <h3 className="text-lg font-black mb-5 text-white">询价提交 / 문의 접수</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold mb-1.5" style={{ color: 'rgba(255,255,255,0.65)' }}>联系人 / 담당자명 <span style={{ color: '#D4A853' }}>*</span></label>
                    <input type="text" name="contact_name" required placeholder="张三 / 홍길동" style={inputStyle} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold mb-1.5" style={{ color: 'rgba(255,255,255,0.65)' }}>联系电话 / 연락처 <span style={{ color: '#D4A853' }}>*</span></label>
                    <input type="tel" name="phone" required placeholder="010-0000-0000" style={inputStyle} />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1.5" style={{ color: 'rgba(255,255,255,0.65)' }}>邮箱 / 이메일 <span style={{ color: '#D4A853' }}>*</span></label>
                  <input type="email" name="email" required placeholder="example@company.com" style={inputStyle} />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1.5" style={{ color: 'rgba(255,255,255,0.65)' }}>公司名称·行业 / 회사명·업종</label>
                  <input type="text" name="company" placeholder="例: 网络销售 / 예: 온라인 판매" style={inputStyle} />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1.5" style={{ color: 'rgba(255,255,255,0.65)' }}>感兴趣的商品 / 관심 상품</label>
                  <select name="interest_category" style={{ ...inputStyle, cursor: 'pointer' }}>
                    <option value="" style={{ background: '#1B2B3A' }}>请选择 / 선택하세요</option>
                    {['汤类 / 탕류','海藻类 / 해조류','饮料·豆乳 / 음료·두유','茶类 / 차류','零食·海苔 / 스낵·김','生活用品 / 생활용품','全部 / 전체','其他咨询 / 기타 문의'].map(c => (
                      <option key={c} value={c} style={{ background: '#1B2B3A' }}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1.5" style={{ color: 'rgba(255,255,255,0.65)' }}>询价内容 / 문의 내용 <span style={{ color: '#D4A853' }}>*</span></label>
                  <textarea
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="请填写感兴趣的商品类别、预计订购数量及交易条件等。/ 관심 상품 카테고리, 예상 주문 수량, 거래 조건 등을 입력해 주세요."
                    onChange={e => setCharCount(e.target.value.length)}
                    style={{ ...inputStyle, resize: 'none' }}
                  />
                  <p className="text-xs text-right mt-1" style={{ color: 'rgba(255,255,255,0.35)' }}>{charCount}/500</p>
                </div>
                {status === 'error' && (
                  <p className="text-xs px-3 py-2 rounded-lg" style={{ color: '#D4A853', backgroundColor: 'rgba(212,168,83,0.1)', border: '1px solid rgba(212,168,83,0.2)' }}>
                    发生错误，请重试。/ 오류가 발생했습니다. 다시 시도해 주세요.
                  </p>
                )}
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full py-3.5 text-sm font-black rounded-xl transition-opacity disabled:opacity-50 cursor-pointer whitespace-nowrap hover:opacity-85"
                  style={{ backgroundColor: '#D4A853', color: '#162233' }}
                >
                  {status === 'submitting' ? '提交中... / 접수 중...' : '提交询价 / 문의 접수하기'}
                </button>
                <p className="text-xs text-center" style={{ color: 'rgba(255,255,255,0.35)' }}>工作人员将在1~2个工作日内与您联系。/ 영업일 기준 1~2일 이내 담당자가 연락드립니다.</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
